/**
 * 智能POS 云MIS 收银台 API（面向店铺员工的小型收银台）
 *
 * 鉴权模型：员工扫码进入 /mis-cashier/<token>，token 为服务端生成的、绑定商户的
 * 「收银台专用 token」（风格同 /hub/<token>），通过 misPay 免登录接口收款，
 * 不使用商户 JWT。token 存于 localStorage，随请求作为参数传递。
 *
 * 相关免登录接口（Merchant.Api）：
 *   GET  /api/misPay/cashier/context   ?token=            店铺信息
 *   GET  /api/misPay/cashier/devices   ?token=            智能POS设备列表
 *   POST /api/misPay/cashier/push       {token,...}        推送收款
 *   GET  /api/misPay/cashier/order     ?token=&payOrderId= 订单状态轮询
 */
import request from '@/http/request'

// 商户端 API 基础地址
const MERCHANT_API_BASE = process.env.VUE_APP_MERCHANT_API_BASE_URL || 'https://merchant.fskeeper.com'

// 收银台 token 在本地的存储键
const TOKEN_KEY = 'mis_cashier_token'

/** 保存收银台 token */
export function setMisToken (token) {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token) } catch (e) { /* ignore */ }
  }
}

/** 读取收银台 token */
export function getMisToken () {
  try { return localStorage.getItem(TOKEN_KEY) || '' } catch (e) { return '' }
}

/** 清除收银台 token */
export function clearMisToken () {
  try { localStorage.removeItem(TOKEN_KEY) } catch (e) { /* ignore */ }
}

/**
 * 收银台上下文（店铺名）
 * GET /api/misPay/cashier/context?token=xxx
 * @returns {Promise<{mchName: string}>}
 */
export function getCashierContext () {
  return request.request({
    baseURL: MERCHANT_API_BASE,
    url: '/api/misPay/cashier/context',
    method: 'GET',
    params: { token: getMisToken() }
  })
}

/**
 * 当前商户智能POS设备列表
 * GET /api/misPay/cashier/devices?token=xxx
 * @returns {Promise<{items: Array, total: number}>}
 */
export function getMchAllDevices () {
  return request.request({
    baseURL: MERCHANT_API_BASE,
    url: '/api/misPay/cashier/devices',
    method: 'GET',
    params: { token: getMisToken(), pageSize: 50 }
  })
}

/**
 * 云MIS 收款推送：先下单+分佣，再推送到POS终端
 * POST /api/misPay/cashier/push
 * @param {object} data
 * @param {number} data.tradeType 交易方式：1-银行卡消费(刷卡) / 3-扫码支付
 * @param {string} data.amount    收款金额（元），如 "8.88"
 * @param {string} [data.deviceNo]  智能POS 设备编号
 * @param {string} [data.appId]     支付应用ID，可留空
 * @param {string} [data.orderInfo] 订单描述
 * @returns {Promise<{payOrderId, mchOrderNo, amount, wayCode, pushSuccess}>}
 */
export function pushMisOrder (data) {
  return request.request({
    baseURL: MERCHANT_API_BASE,
    url: '/api/misPay/cashier/push',
    method: 'POST',
    data: Object.assign({ token: getMisToken() }, data)
  })
}

/**
 * 查询支付订单（轮询收款结果）
 * GET /api/misPay/cashier/order?token=xxx&payOrderId=xxx
 * @param {string} payOrderId
 * @returns {Promise<{payOrderId, state, amount, errMsg}>} state：0-生成 1-支付中 2-成功 3-失败 6-关闭
 */
export function queryMisOrder (payOrderId) {
  return request.request({
    baseURL: MERCHANT_API_BASE,
    url: '/api/misPay/cashier/order',
    method: 'GET',
    params: { token: getMisToken(), payOrderId }
  })
}

export default {
  setMisToken,
  getMisToken,
  clearMisToken,
  getCashierContext,
  getMchAllDevices,
  pushMisOrder,
  queryMisOrder
}
